import {Match} from "@/modules/match/entity";
import chance from "@/modules/base/services/chance";
import {inject, injectable} from "inversify";
import AbstractMatchEvaluator from "./AbstractMatchEvaluator";
import {CardEnum, NeutralIncidentTypeEnum} from "@/modules/match/enums";
import {Player, Team} from "@/modules/team/entity";
import {Collection} from "collect.js";
import {HomeAwayType} from "@/modules/match/types";
import {ConfigRepository} from "@/modules/base/services/ConfigRepository";
import LineupSkillCalculator from "@/modules/team/services/LineupSkillCalculator";
import EffectiveLineupSkillCalculator from "@/modules/team/services/EffectiveLineupSkillCalculator";
import LaneBasedGoalsHandler from "@/modules/match/services/live-match-handlers/LaneBasedGoalsHandler";

@injectable()
export default class SkillBasedMatchEvaluator extends AbstractMatchEvaluator {
  private readonly playerCountLimit: number;
  private readonly averageYellowCardPerMatch: number;
  private readonly averageRedPerMatch: number;

  private readonly minimumPenalties: number = 5;

  constructor(
    @inject(ConfigRepository) protected configRepository: ConfigRepository,
    @inject(LineupSkillCalculator) protected lineupSkillCalculator: LineupSkillCalculator,
    @inject(EffectiveLineupSkillCalculator) protected effectiveLineupSkillCalculator: EffectiveLineupSkillCalculator,
  ) {
    super(configRepository, lineupSkillCalculator, effectiveLineupSkillCalculator);

    this.playerCountLimit = configRepository.get('match.evaluation.playerCountLimit');
    this.averageYellowCardPerMatch = configRepository.get('match.evaluation.averageYellowCardPerMatch');
    this.averageRedPerMatch = configRepository.get('match.evaluation.averageRedPerMatch');
  }

  async evaluate(match: Match): Promise<void> {
    await this.prepare(match);

    await this.createNeutralIncident(match, NeutralIncidentTypeEnum.START, 0);

    const teams: { [key in HomeAwayType]: Team } = {
      home: match.homeTeam,
      away: match.awayTeam,
    };

    const players: { [key in HomeAwayType]: Collection<Player> } = {
      home: collect<Player>(match.homeTeam.players).filter(p => !!p.lineup),
      away: collect<Player>(match.awayTeam.players).filter(p => !!p.lineup),
    };

    const skills: { [key in HomeAwayType]: number } = {
      home: players.home.sum('skill') as number,
      away: players.away.sum('skill') as number,
    };

    const totalChances = this.configRepository.get('match.evaluation.skillBased.totalChances')(match);

    let totalMinutes = 90 + chance.integer({min: 1, max: 5});
    let minute = 1;

    for (; minute <= totalMinutes; minute++) {
      if (chance.bool({likelihood: totalChances / totalMinutes * 100})) {
        const homeAway: HomeAwayType = chance.bool({likelihood: this.getLikelihood(skills.home, skills.away)}) ? 'home' : 'away';
        const homeAwayOpp: HomeAwayType = homeAway === 'home' ? 'away' : 'home';
        const player = chance.pickoneByFieldProbability(players[homeAway], (p: Player) => p.lineup! - 1);
        const goalkeeper = players[homeAwayOpp].first((p: Player) => p.lineup === 1);

        const playerSkill = (100 - LaneBasedGoalsHandler.gkChance) / 100 * (player ? player.skill : 0);
        const goalkeeperSkill = goalkeeper.skill ? goalkeeper.skill : 0;
        const likelihoodGoal = this.getLikelihood(playerSkill, goalkeeperSkill);

        this.addChance(match, homeAway);

        if (chance.bool({likelihood: likelihoodGoal})) {
          await this.createGoalIncident(match, teams[homeAway], teams[homeAwayOpp], minute, player);
          this.addGoal(match, homeAway);
        } else {
          await this.createMissedGoalIncident(match, teams[homeAway], player, minute);
        }
      }

      if (chance.bool({likelihood: this.averageYellowCardPerMatch / totalMinutes * 100})) {
        const homeAway: HomeAwayType = chance.bool() ? 'home' : 'away';
        const player = chance.pickone(players[homeAway].toArray()) as Player;
        await this.createCardIncident(match, teams[homeAway], player, CardEnum.YELLOW, minute);
      }

      if (chance.bool({likelihood: this.averageRedPerMatch / totalMinutes * 100})) {
        const homeAway: HomeAwayType = chance.bool() ? 'home' : 'away';
        const player = chance.pickone(players[homeAway].toArray()) as Player;
        await this.createCardIncident(match, teams[homeAway], player, CardEnum.RED, minute);
      }

      // todo substitutions

      if (!match.extraTime && minute === totalMinutes) {
        await this.createNeutralIncident(match, NeutralIncidentTypeEnum.END, minute);

        if (match.needDecision && !this.isDecided(match)) {
          totalMinutes = 120;
          minute = 90;
          match.extraTime = true;

          await this.createNeutralIncident(match, NeutralIncidentTypeEnum.START_EXTRA_TIME, minute);
        }
      }

      if (match.extraTime && minute === totalMinutes) {
        await this.createNeutralIncident(match, NeutralIncidentTypeEnum.END_EXTRA_TIME, minute);

        if (!this.isDecided(match)) {
          const playerSort = (p: Player) => p.lineup === 1 ? 0 : this.getPlayerSkill(match, p);

          await this.processPenalty(
            match,
            {
              home: players.home.sortBy(playerSort),
              away: players.away.sortBy(playerSort),
            },
            {
              home: match.homeTeam,
              away: match.awayTeam,
            },
            minute
          );
        }

      }
    }

    await this.processResult(match);
    console.debug(`Match evaluated`, match);
  }

  private async processPenalty(
    match: Match,
    players: { [key in HomeAwayType]: Collection<Player> },
    teams: { [key in HomeAwayType]: Team },
    minute: number
  ) {
    match.penalty = true;
    match.homePenaltyGoals = 0;
    match.awayPenaltyGoals = 0;

    await this.createNeutralIncident(match, NeutralIncidentTypeEnum.START_PENALTY, minute);

    let round = 0;
    let potentialGoals = this.minimumPenalties;
    const penalties: { [key in HomeAwayType]: number } = {home: 0, away: 0};
    const order = chance.bool() ? ['home', 'away'] : ['away', 'home'];
    let shouldBreak = false;
    let isDecided = false;

    do {
      potentialGoals = Math.max(1, this.minimumPenalties - round);

      for (let item of order) {
        if (item === 'home') {
          const homeWin = penalties.home > penalties.away + potentialGoals;
          const awayWin = penalties.home < penalties.away - potentialGoals;
          shouldBreak = homeWin || awayWin;
        } else {
          const homeWin = penalties.home > penalties.away + potentialGoals;
          const awayWin = penalties.home < penalties.away - potentialGoals;
          shouldBreak = homeWin || awayWin;
        }

        if (shouldBreak) {
          break;
        }

        const homeAway = item as HomeAwayType;
        const homeAwayOpp = homeAway === 'home' ? 'away' : 'home';
        const player = players[homeAway].get(round % players[homeAway].count());
        const goalKeeper = players[homeAwayOpp].first(p => p.lineup === 1);
        const playerSkill = player ? this.getPlayerSkill(match, player)! : 0;
        const gkSkill = goalKeeper ? this.getPlayerSkill(match, goalKeeper)! : 1;
        const likelihoodGoal = this.getLikelihood(gkSkill, playerSkill);

        let incident;

        if (chance.bool({likelihood: likelihoodGoal})) {
          incident = await this.createGoalIncident(match, teams[homeAway], teams[homeAwayOpp], minute, player!, true);
          penalties[homeAway] = penalties[homeAway] + 1;
          this.addPenaltyGoal(match, homeAway);
        } else {
          incident = await this.createMissedGoalIncident(match, teams[homeAway], player!, minute);
          this.addChance(match, homeAway);
        }

        incident.penalty = true;
        await incident.save();
      }

      round++;

      isDecided = penalties.home > penalties.away + potentialGoals - 1
        || penalties.away > penalties.home + potentialGoals - 1;
    } while (!isDecided);

    await this.createNeutralIncident(match, NeutralIncidentTypeEnum.END_PENALTY, minute);
  }

  getPlayerSkill(match: Match, player: Player) {
    return this.effectiveLineupSkillCalculator.calculateLinedupSkillPlayerByTeam(
      player.teamId === match.homeTeam.id ? match.homeTeam : match.awayTeam,
      player
    );
  }

  private getLikelihood(x: number, y: number) {
    return Math.min(100, x / (x + y) * 100);
  }

  private addGoal(match: Match, homeAway: HomeAwayType) {
    homeAway === 'home'
      ? match.homeGoals = match.homeGoals! + 1
      : match.awayGoals = match.awayGoals! + 1;
  }

  private addChance(match: Match, homeAway: HomeAwayType) {
    homeAway === 'home'
      ? match.homeChances = match.homeChances! + 1
      : match.awayChances = match.awayChances! + 1;
  }

  private addPenaltyGoal(match: Match, homeAway: HomeAwayType) {
    this.addGoal(match, homeAway);

    homeAway === 'home'
      ? match.homePenaltyGoals = match.homePenaltyGoals! + 1
      : match.awayPenaltyGoals = match.awayPenaltyGoals! + 1;
  }

  private isDecided(match: Match) {
    if (!match.connectedMatch) {
      return match.homeGoals !== match.awayGoals;
    }

    return (match.homeGoals! + match.connectedMatch.awayGoals!) !== (match.awayGoals! + match.connectedMatch.homeGoals!);
  }
}